import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform, type MotionValue } from 'framer-motion';
import { ArrowRightIcon, VideoIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { useOverlayActions } from '../../contexts/StoreContext';
import { founder } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { ease } from '../../core/format/motion';
import { mediaOr } from '../../core/rules/media';
import type { Media } from '../../core/types/media';
import { fill } from '../../utils/copy';
import { belowHeader, eyebrowCls, quietLink, sectionHeading } from '../../ui/sections';
import { btnOnClay } from '../../ui/styles';
import { segmentFill, stepAt, trackHeight } from './storyScene';

/** Lager dan dit (telefoon liggend) is vastzetten krap: dan staan de stappen gewoon onder elkaar. */
const TALL_ENOUGH = '(min-height: 560px)';

/**
 * "Zo werk ik": de werkwijze als vastgezette scène, op salie. Het hart van het verhaal.
 *
 * - De sectie schuift als een kaart óver de vorige heen (ronde bovenhoeken, zachte schaduw).
 * - Zodra de scène het scherm vult, zet hij zich vast (net onder de header). Scrollen loopt dan
 *   door Kijken, Kiezen, Opbouwen: foto en tekst wisselen samen, drie balkjes tonen waar je bent.
 *   Op elk scherm hetzelfde; computer foto links en tekst rechts, smaller foto boven en tekst eronder.
 * - Minder beweging of een heel laag scherm: niets vastgezet, de stappen onder elkaar met hun foto.
 * - Daarna de volgende stap: het huidadvies (dat ís Kijken), met een afspraak en het consult rustig eronder.
 *
 * Beelden per stap uit Paginateksten (home.story.steps[].media); leeg = het salonbeeld, het
 * portret met producten, het portret buiten. Shell op salie 5,4:1.
 */
export function HomeStory() {
  const pages = usePages();
  const {
    policy
  } = useSite();
  const {
    openBooking,
    openQuiz
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const tall = useMediaQuery(TALL_ENOUGH);
  const copy = pages.home.story;
  const steps = copy.steps;
  const pinned = !reduce && tall && steps.length > 1;
  const fallbacks: Media[] = [mediaOr(copy.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een behandeling in de salon`
  }), {
    image: founder.withProducts,
    alt: `${founder.name} met de producten die ze kiest`,
    focus: {
      x: 50,
      y: 35
    }
  }, {
    image: founder.portraitPalm,
    alt: founder.name,
    focus: {
      x: 50,
      y: 30
    }
  }];
  const imageFor = (i: number): Media => mediaOr(steps[i]?.media ?? {
    image: '',
    alt: ''
  }, fallbacks[i % fallbacks.length]);
  return <section className="relative z-raised -mt-8 rounded-t-[2rem] bg-sage text-shell shadow-[0_-24px_48px_-28px_rgba(36,31,28,0.35)] lg:-mt-14 lg:rounded-t-[3rem]" aria-labelledby="home-story" data-id="element-7510" data-mp="1pg3wpbisc3t9">
      <div className="mx-auto max-w-content px-6 pt-20 sm:pt-24 lg:px-10 lg:pt-32" data-id="element-7511" data-mp="1syjfg3mgs9lq">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-16" data-id="element-7512" data-mp="1wzlg8ow14tpt">
          <div className="lg:col-span-7" data-id="element-7513" data-mp="1wxs8t7dle5gr">
            {copy.eyebrow && <p className={`${eyebrowCls} text-shell`} data-id="element-7514" data-mp="21q1ta7vxudbn">{fill(copy.eyebrow)}</p>}
            <h2 id="home-story" className={`mt-5 ${sectionHeading}`} data-id="element-7515" data-mp="26p51g177sznu">
              <HeadingText heading={copy.heading} accentBlock accentClassName="text-blush" data-id="element-7516" data-mp="26ris7xuv3ppl" />
            </h2>
          </div>
          {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-shell lg:col-span-5 lg:self-end" data-id="element-7517" data-mp="1e07h7sjg0ssg">{fill(copy.body)}</p>}
        </div>
      </div>

      {steps.length > 0 && (pinned ? <PinnedSteps steps={steps.map((s, i) => ({
      title: fill(s.title),
      body: fill(s.body),
      media: imageFor(i)
    }))} data-id="element-7518" data-mp="1nzg8gdmxbtb4" /> : <StackedSteps steps={steps.map((s, i) => ({
      title: fill(s.title),
      body: fill(s.body),
      media: imageFor(i)
    }))} data-id="element-7519" data-mp="2ue7fz06jyce4" />)}

      <div className="mx-auto max-w-content px-6 pb-20 sm:pb-24 lg:px-10 lg:pb-32" data-id="element-7520" data-mp="2zg6h03etkx7w">
        <div className="flex flex-col gap-3 border-t border-shell/25 pt-10 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-8 sm:gap-y-3" data-id="element-7521" data-mp="c5wdl6xr7u23">
          {/* Wie net heeft gezien hoe Sarah werkt, is klaar voor de salon: dat is de knop. */}
          {copy.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={`${btnOnClay} group`} data-id="element-7522" data-mp="g66jkjljqkgy">
              {fill(copy.cta)}
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7523" data-mp="q4w30gt3cczb" />
            </button>}
          {copy.quizCta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={`${quietLink} justify-center text-shell sm:justify-start`} data-id="element-7524" data-mp="zxbjf9oqoti3">
              {fill(copy.quizCta)}
            </button>}
          {copy.aboutCta && <Link to="/over-ons" className={`${quietLink} justify-center text-shell sm:justify-start`} data-id="element-7525" data-mp="1shfe6wvxe8od">
              {fill(copy.aboutCta)}
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7526" data-mp="1xg0boojsme94" />
            </Link>}
          {copy.secondaryCta && <Link to="/behandelingen" className={`${quietLink} justify-center text-shell sm:justify-start`} data-id="element-7527" data-mp="1nhszsk4d2mdz">
              {fill(copy.secondaryCta)}
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7528" data-mp="19p4d98ku0ksw" />
            </Link>}
        </div>
        {copy.consultLine && <button type="button" onClick={() => openBooking(policy.consult.slug)} aria-haspopup="dialog" className="mt-4 inline-flex min-h-[44px] items-start gap-3 text-left text-[15px] leading-relaxed text-shell underline-offset-4 hover:underline" data-id="element-7529" data-mp="zu2oweydrmrd">
            <VideoIcon className="mt-1 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-7530" data-mp="usn66tjodsc5" />
            {fill(copy.consultLine)}
          </button>}
      </div>
    </section>;
}
type SceneStep = {
  title: string;
  body: string;
  media: Media;
};

/**
 * De vastgezette scène. Het spoor is één scherm plus ± 0,8 scherm per stap hoog; daarin blijft
 * het beeld staan terwijl je scrolt. Alle stappen staan in de lijst (schermlezers lezen ze
 * allemaal); alleen de actieve is zichtbaar.
 */
function PinnedSteps({
  steps
}: {
  steps: SceneStep[];
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const {
    scrollYProgress
  } = useScroll({
    target: trackRef,
    offset: ['start start', 'end end']
  });
  useMotionValueEvent(scrollYProgress, 'change', v => {
    const next = stepAt(v, steps.length);
    setActive(prev => prev === next ? prev : next);
  });
  return <div ref={trackRef} style={{
    height: trackHeight(steps.length)
  }} className="relative mt-12 lg:mt-16" data-id="element-7531" data-mp="1v3g1ieg5xj6w">
      <div className={`sticky ${belowHeader} h-[calc(100svh-var(--header-offset,0px))]`} data-id="element-7532" data-mp="1r2meroegiafl">
        <div className="mx-auto grid h-full max-w-content grid-rows-[minmax(0,1fr)_auto] gap-6 px-6 py-6 lg:grid-cols-12 lg:grid-rows-1 lg:items-center lg:gap-16 lg:px-10 lg:py-10" data-id="element-7533" data-mp="6kbwdy0scoey">
          <StepFrame steps={steps} active={active} className="h-full min-h-0 lg:col-span-6 lg:h-[min(100%,780px)]" data-id="element-7534" data-mp="fidti2heewwy" />

          <div className="lg:col-span-6" data-id="element-7535" data-mp="kb6x2n14rz7g">
            {/* Waar je bent: één balkje per stap, dat meeloopt met het scrollen. */}
            <div aria-hidden="true" className="flex gap-2" data-id="element-7536" data-mp="u81suy60rli1">
              {steps.map((s, i) => <Segment key={`${i}-${s.title}`} progress={scrollYProgress} count={steps.length} index={i} data-id="element-7537" data-mp="y9ncd2d26y17" data-mpi={i} />)}
            </div>
            {/* Alle stappen op dezelfde plek gestapeld: de hoogte is die van de langste, niets verspringt. */}
            <ol className="mt-6 grid lg:mt-10" data-id="element-7538" data-mp="302kzchk5mwe8">
              {steps.map((s, i) => {
              const isActive = i === active;
              return <motion.li key={`${i}-${s.title}`} className={`[grid-area:1/1] ${isActive ? '' : 'pointer-events-none'}`} initial={false} animate={{
                opacity: isActive ? 1 : 0,
                y: isActive ? 0 : i < active ? -12 : 12
              }} transition={{
                duration: 0.25,
                ease
              }} data-id="element-7539" data-mp="1arr2zt3vwd89" data-mpi={i}>
                    <StepText index={i} title={s.title} body={s.body} data-id="element-7540" data-mp="rtevebsmfbes" />
                  </motion.li>;
            })}
            </ol>
          </div>
        </div>
      </div>
    </div>;
}

/** Zonder vastzetten: elke stap met zijn foto, onder elkaar. */
function StackedSteps({
  steps
}: {
  steps: SceneStep[];
}) {
  return <ol className="mx-auto mt-12 grid max-w-content gap-14 px-6 pb-14 lg:mt-16 lg:gap-20 lg:px-10 lg:pb-20" data-id="element-7541" data-mp="wrgbcnur2um1">
      {steps.map((s, i) => <li key={`${i}-${s.title}`} className="grid gap-6 lg:grid-cols-12 lg:items-center lg:gap-16" data-id="element-7542" data-mp="2d8uc32mm2lem" data-mpi={i}>
          <MediaView media={s.media} sizes="(min-width: 1024px) 45vw, 100vw" className="aspect-[4/3] w-full overflow-hidden lg:col-span-6" data-id="element-7543" data-mp="28a0xt7zi2qde" />
          <div className="lg:col-span-6" data-id="element-7544" data-mp="3v4mmy0t8zvd3">
            <StepText index={i} title={s.title} body={s.body} data-id="element-7545" data-mp="3v5o0cbxqktxz" />
          </div>
        </li>)}
    </ol>;
}

/**
 * Het kader staat stil; alleen de foto erin wisselt. De nieuwe opent als een gordijn van onderen
 * (de foto zelf valt zacht op zijn plek), terug scrollen sluit hem weer.
 */
function StepFrame({
  steps,
  active,
  className
}: {
  steps: SceneStep[];
  active: number;
  className: string;
}) {
  return <div className={`relative overflow-hidden rounded-[1.25rem] bg-clay ${className}`} data-id="element-7546" data-mp="hravxr593wlx">
      {steps.map((s, i) => <motion.div key={`${i}-${s.title}`} className="absolute inset-0" initial={false} animate={{
      clipPath: i <= active ? 'inset(0% 0% 0% 0%)' : 'inset(100% 0% 0% 0%)'
    }} transition={{
      duration: i === 0 ? 0 : 0.3,
      ease
    }} aria-hidden={i !== active} data-id="element-7547" data-mp="8wwjdwc3v06i" data-mpi={i}>
          <motion.div className="h-full w-full" initial={false} animate={{
        scale: i === active ? 1 : 1.05
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-7548" data-mp="11qvhxb78u010">
            <MediaView media={s.media} sizes="(min-width: 1024px) 45vw, 100vw" className="h-full w-full" data-id="element-7549" data-mp="16o5d77hbd5wr" />
          </motion.div>
        </motion.div>)}
    </div>;
}
function Segment({
  progress,
  count,
  index
}: {
  progress: MotionValue<number>;
  count: number;
  index: number;
}) {
  const fillX = useTransform(progress, v => segmentFill(v, count, index));
  return <span className="relative block h-0.5 flex-1 overflow-hidden bg-shell/25" data-id="element-7550" data-mp="ns5hpmp4nk8f">
      <motion.span style={{
      scaleX: fillX
    }} className="absolute inset-0 origin-left bg-shell" data-id="element-7551" data-mp="93iu6xpcjm64" />
    </span>;
}

/** Cijfer, titel en uitleg van een stap. De lijst is al genummerd voor schermlezers. */
function StepText({
  index,
  title,
  body
}: {
  index: number;
  title: string;
  body: string;
}) {
  return <>
      <div className="flex items-baseline gap-4" data-id="element-7552" data-mp="4zkwmrfo6jf6">
        <span aria-hidden="true" className="type-section leading-none text-blush" data-id="element-7553" data-mp="3hkgq0t6otiyb">
          {index + 1}
        </span>
        <h3 className="type-sub leading-none" data-id="element-7554" data-mp="3clx9576yaxct">{title}</h3>
      </div>
      <p className="mt-4 max-w-md text-[17px] leading-relaxed text-shell lg:mt-6" data-id="element-7555" data-mp="3cnqaq82pirdy">{body}</p>
    </>;
}